import { Badge, Box, Flex, Heading, HStack, Icon, Skeleton, Stack, Text } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { LuArrowRight, LuCalendarPlus, LuCheck, LuClipboardCheck, LuClock, LuFilePlus2, LuPlus, LuSettings, LuTriangleAlert } from 'react-icons/lu';
import type { User } from '@/api/User';
import { AdminInhabitant } from '@/api/Admin';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { OrgUtil } from '@/data/OrgUtil';
import { formatDay, formatMinutes } from '@/data/DateTimeUtil';
import { useI18n } from '@/i18n/I18nContext';
import { useApprovalInbox } from './approvals/ApprovalInboxContext';

const HomeWorkspace = ({ currentUser }: { currentUser: User }) => {
    const { t } = useI18n();
    const { summary, isLoading, error, refresh } = useApprovalInbox();
    const actions = [
        { label: 'logTime', to: '/timesheet', icon: LuClock },
        { label: 'requestAbsence', to: '/timesheet/absences', icon: LuCalendarPlus },
        { label: 'addExpense', to: '/partners/expenses/add', icon: LuFilePlus2 },
        { label: 'newProject', to: '/projects/add', icon: LuPlus },
        ...(OrgUtil.isAllowed(currentUser.role, AdminInhabitant)
            ? [{ label: 'organization', to: '/organization', icon: LuSettings }]
            : []),
    ];

    return (
        <Stack gap='1' mt='2' mb='8'>
            <Text color='fg.muted'>{t('landing.workspace.subtitle')}</Text>
            <Flex direction={{ base: 'column', lg: 'row' }} align='stretch' gap='2'>
                <Box as='section' aria-labelledby='home-quick-actions' flex='1' minW='0' w='full' borderWidth='1px' borderColor='border.subtle' rounded='xl' bg='bg.panel' p='1'>
                    <Heading as='h2' id='home-quick-actions' size='lg' mb='4'>{t('landing.workspace.quickActions')}</Heading>
                    <Flex gap='3' wrap='wrap'>
                        {actions.map(({ label, to, icon: ActionIcon }, index) => (
                            <Button key={label} asChild variant={index === 0 ? 'solid' : 'outline'}>
                                <Link to={to}><ActionIcon aria-hidden='true' />{t(`landing.workspace.${label}`)}</Link>
                            </Button>
                        ))}
                    </Flex>
                </Box>

                <Box as='section' aria-labelledby='home-attention' flex='1' minW='0' w='full' borderWidth='1px' borderColor='border.subtle' rounded='xl' bg='bg.panel' p='1'>
                    <Heading as='h2' id='home-attention' size='lg' mb='4'>{t('landing.workspace.attention')}</Heading>
                    {isLoading ? (
                        <Stack aria-label={t('landing.workspace.loading')} aria-busy='true' gap='3'>
                            <Skeleton height='16' /><Skeleton height='16' />
                        </Stack>
                    ) : error ? (
                        <Alert status='error' title={t('approvals.loadError')}>
                            <Button variant='outline' size='sm' mt='2' onClick={() => refresh()}>{t('landing.workspace.retry')}</Button>
                        </Alert>
                    ) : (
                        <Stack gap='3'>
                            {summary.actionRequiredTotal > 0 && (
                                <Button asChild variant='outline' colorPalette='orange' h='auto' minH='16' py='3' whiteSpace='normal' justifyContent='start'>
                                    <Link to='/approvals?tab=actionRequired'>
                                        <LuTriangleAlert aria-hidden='true' />
                                        <Box flex='1' textAlign='start'>
                                            <Text fontWeight='semibold'>{t('landing.workspace.returned')}</Text>
                                            <Text fontSize='sm' fontWeight='normal' color='fg.muted'>{t('landing.workspace.returnedHint')}</Text>
                                        </Box>
                                        <Badge colorPalette='orange'>{summary.actionRequiredTotal}</Badge><LuArrowRight aria-hidden='true' />
                                    </Link>
                                </Button>
                            )}
                            {summary.pendingTimesheets > 0 && (
                                <Button asChild variant='outline' h='auto' minH='16' py='3' whiteSpace='normal' justifyContent='start'>
                                    <Link to='/approvals?tab=timesheets'>
                                        <LuClipboardCheck aria-hidden='true' />
                                        <Box flex='1' textAlign='start'>
                                            <Text fontWeight='semibold'>{t('landing.workspace.pendingTimesheets')}</Text>
                                            <Text fontSize='sm' fontWeight='normal' color='fg.muted'>
                                                {t('landing.workspace.pendingTimesheetsHint', { hours: formatMinutes(summary.pendingTimesheetMinutes) })}
                                            </Text>
                                        </Box>
                                        <Badge colorPalette='blue'>{summary.pendingTimesheets}</Badge><LuArrowRight aria-hidden='true' />
                                    </Link>
                                </Button>
                            )}
                            {summary.pendingAbsences > 0 && (
                                <Button asChild variant='outline' colorPalette='green' h='auto' minH='16' py='3' whiteSpace='normal' justifyContent='start'>
                                    <Link to='/approvals?tab=absences'>
                                        <LuClipboardCheck aria-hidden='true' />
                                        <Box flex='1' textAlign='start'>
                                            <Text fontWeight='semibold'>{t('landing.workspace.pendingAbsences')}</Text>
                                            <Text fontSize='sm' fontWeight='normal' color='fg.muted'>
                                                {t('landing.workspace.pendingAbsencesHint', { days: formatDay(summary.pendingAbsenceDays) })}
                                            </Text>
                                        </Box>
                                        <Badge colorPalette='teal'>{summary.pendingAbsences}</Badge><LuArrowRight aria-hidden='true' />
                                    </Link>
                                </Button>
                            )}
                            {summary.pendingTotal === 0 && summary.actionRequiredTotal === 0 && (
                                <HStack gap='3' py='2' align='start'>
                                    <Icon as={LuCheck} color='green.fg' boxSize='5' mt='1' />
                                    <Box>
                                        <Text fontWeight='medium'>{t('landing.workspace.allClear')}</Text>
                                        <Text fontSize='sm' color='fg.muted'>{t('landing.workspace.allClearHint')}</Text>
                                    </Box>
                                </HStack>
                            )}
                        </Stack>
                    )}
                </Box>
            </Flex>
        </Stack>
    );
};

export default HomeWorkspace;
